import {
  AccurateDateIntervalWithShift,
  Activity,
  OperationStatus,
  PublicationStatus,
  ShiftCategoriesIdsMap,
  Shift,
  WilsonApp,
  WithPreparedAttributes,
  ShiftState,
  LocationAndDateTimeRange,
  TimelineShift,
} from '@wilson/interfaces';
import { createFullActivityObject } from '@wilson/non-domain-specific/activities-helpers/services';
import { addMinutes, format, subMinutes } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { DateTimeFormat } from '@wilson/utils';
import { cloneDeep } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { LocationsStoreService } from '@wilson/locations';

export function createTransferShiftForNextItem({
  nextShift,
  shiftViews,
  duration,
  selectedActivityCategoryId,
  isPreviousItemShift,
  previousItem,
  nextShiftWithActivitiesToUpdate,
  homeLocationId,
}: {
  nextShift: AccurateDateIntervalWithShift['shift'] | undefined;
  previousItem: LocationAndDateTimeRange & {
    shiftId?: string;
  };
  shiftViews: AccurateDateIntervalWithShift[];
  isPreviousItemShift: boolean;
  duration: number;
  selectedActivityCategoryId: string;
  homeLocationId: string | null | undefined;
  nextShiftWithActivitiesToUpdate:
    | (Shift & {
        id: string;
        activities: Activity[];
      })
    | null;
}): {
  shiftWithoutActivities: Shift & WithPreparedAttributes & { id: string };
  oldShift: TimelineShift;
  newActivity: Activity;
  nextShiftWithActivitiesToUpdate: Shift & {
    id: string;
    activities: Activity[];
  };
} | null {
  const previousItemEndLocationId = isPreviousItemShift
    ? shiftViews.find(
        (shiftView) => shiftView.shift.id === previousItem.shiftId,
      )?.shift.endLocationId
    : homeLocationId;

  if (
    nextShift?.id &&
    nextShiftWithActivitiesToUpdate?.id &&
    previousItemEndLocationId
  ) {
    const nextItemStartLocationId = nextShift.startLocationId;

    const newActivity: Activity = createFullActivityObject({
      activityCategoryId: selectedActivityCategoryId,
      createdFrom: WilsonApp.Portal,
      jobId: null,
      name: '',
      operationalStatus: OperationStatus.NotStarted,
      serviceId: null,
      shiftId: nextShiftWithActivitiesToUpdate.id,
      viaLocationIds: [],
      startDatetime: subMinutes(
        new Date(nextShift.startDatetime),
        duration,
      ).toISOString(),
      endDatetime: nextShift.startDatetime,
      startLocationId: previousItemEndLocationId,
      endLocationId: nextItemStartLocationId,
    });
    nextShiftWithActivitiesToUpdate.activities.unshift(newActivity);

    const shiftWithoutActivities: Shift &
      WithPreparedAttributes & {
        id: string;
      } = {
      ...nextShift,
      startDatetime: newActivity.startDatetime,
      startLocationId: previousItemEndLocationId,
    };

    return {
      shiftWithoutActivities,
      oldShift: cloneDeep(nextShift),
      newActivity,
      nextShiftWithActivitiesToUpdate,
    };
  } else {
    console.error('No locationId provided or shiftToUpdate not found');
    return null;
  }
}

export function createTransferShiftForPreviousItem({
  previousShift,
  shiftViews,
  duration,
  selectedActivityCategoryId,
  isNextItemShift,
  nextItem,
  homeLocationId,
  previousShiftWithActivitiesToUpdate,
}: {
  previousShift: AccurateDateIntervalWithShift['shift'] | undefined;
  shiftViews: AccurateDateIntervalWithShift[];
  duration: number;
  selectedActivityCategoryId: string;
  isNextItemShift: boolean;
  nextItem: LocationAndDateTimeRange & { shiftId?: string };
  homeLocationId: string | null | undefined;
  previousShiftWithActivitiesToUpdate:
    | (Shift & {
        id: string;
        activities: Activity[];
      })
    | null;
}): {
  shiftWithoutActivities: Shift & WithPreparedAttributes & { id: string };
  oldShift: TimelineShift;
  newActivity: Activity;
  previousShiftWithActivitiesToUpdate: Shift & {
    id: string;
    activities: Activity[];
  };
} | null {
  const nextItemStartLocationId = isNextItemShift
    ? shiftViews.find((shiftView) => shiftView.shift.id === nextItem.shiftId)
        ?.shift.startLocationId
    : homeLocationId;
  if (
    previousShift?.id &&
    previousShiftWithActivitiesToUpdate &&
    nextItemStartLocationId
  ) {
    const oldShift = cloneDeep(previousShift);

    const newActivity: Activity = createFullActivityObject({
      activityCategoryId: selectedActivityCategoryId,
      createdFrom: WilsonApp.Portal,
      jobId: null,
      name: '',
      operationalStatus: OperationStatus.NotStarted,
      serviceId: null,
      shiftId: previousShift.id || null,
      viaLocationIds: [],
      startDatetime: previousShift.endDatetime,
      endDatetime: addMinutes(
        new Date(previousShift.endDatetime),
        duration,
      ).toISOString(),
      startLocationId: previousShift.endLocationId,
      endLocationId: nextItemStartLocationId,
    });
    previousShiftWithActivitiesToUpdate.activities.push(newActivity);

    const shiftWithoutActivities: Shift &
      WithPreparedAttributes & {
        id: string;
      } = {
      ...previousShift,
      endDatetime: newActivity.endDatetime,
      endLocationId: nextItemStartLocationId,
    };
    return {
      shiftWithoutActivities,
      oldShift,
      newActivity,
      previousShiftWithActivitiesToUpdate,
    };
  } else {
    console.error('No locationId provided or shiftToUpdate not found');
    return null;
  }
}

export async function createTransferShift({
  shiftViews,
  previousItem,
  nextItem,
  duration,
  selectedActivityCategoryId,
  dateTime,
  organizationalUnitId,
  homeLocationId,
  locationsStoreService,
  userId,
  translate,
}: {
  shiftViews: AccurateDateIntervalWithShift[];
  previousItem: LocationAndDateTimeRange & {
    shiftId?: string;
  };
  nextItem: LocationAndDateTimeRange & {
    shiftId?: string;
  };
  duration: number;
  selectedActivityCategoryId: string;
  dateTime: Date;
  organizationalUnitId: string;
  userId: string;
  homeLocationId: string | null | undefined;
  locationsStoreService: LocationsStoreService;
  translate: TranslateService;
}): Promise<{
  shiftWithoutActivities: TimelineShift;
  newShift: Shift & { activities: Activity[] };
} | null> {
  const previousShiftEndLocationId =
    shiftViews.find((shiftView) => shiftView.shift.id === previousItem.shiftId)
      ?.shift.endLocationId || homeLocationId;
  const nextShiftLocationId =
    shiftViews.find((shiftView) => shiftView.shift.id === nextItem.shiftId)
      ?.shift.startLocationId || homeLocationId;

  if (previousShiftEndLocationId && nextShiftLocationId) {
    const previousShiftEndLocation = (await firstValueFrom(
      locationsStoreService.getLocationFromCache(previousShiftEndLocationId),
    )) as { locationCode: string; name: string };

    const nextShiftStartLocation = (await firstValueFrom(
      locationsStoreService.getLocationFromCache(nextShiftLocationId),
    )) as { locationCode: string; name: string };

    const newShift: Shift & { activities: Activity[] } = {
      comment: null,
      planningComment: null,
      name: `${translate.instant('page.shift_timeline.transfer_shift')} ${
        previousShiftEndLocation?.locationCode
      }-${nextShiftStartLocation?.locationCode}`,
      organizationalUnitId: organizationalUnitId,
      publicationStatus: PublicationStatus.NotPublished,
      shiftCategoryId: ShiftCategoriesIdsMap.Normal,
      startDate: format(dateTime, DateTimeFormat.DatabaseDateFormat),
      state: ShiftState.NotSubmitted,
      userId: userId,
      activities: [
        createFullActivityObject({
          activityCategoryId: selectedActivityCategoryId,
          createdFrom: WilsonApp.Portal,
          jobId: null,
          name: '',
          operationalStatus: OperationStatus.NotStarted,
          serviceId: null,
          shiftId: null,
          viaLocationIds: [],
          startDatetime: dateTime.toISOString(),
          endDatetime: addMinutes(dateTime, duration).toISOString(),
          startLocationId: previousShiftEndLocationId,
          endLocationId: nextShiftLocationId,
        }),
      ],
    };
    // eslint-disable-next-line
    const { activities, ...shiftToCreate } = newShift;
    const tempShiftId = crypto.randomUUID();
    const shiftWithoutActivities: TimelineShift = {
      ...shiftToCreate,
      deviatedEndDatetime: null,
      deviatedStartDatetime: null,
      startDatetime: dateTime.toISOString(),
      endDatetime: addMinutes(dateTime, duration).toISOString(),
      endLocation: nextShiftStartLocation,
      endLocationId: nextShiftLocationId,
      operationalStatus: OperationStatus.NotStarted,
      reportedEndDatetime: null,
      reportedEndLocation: null,
      reportedEndLocationId: null,
      reportedStartDatetime: null,
      reportedStartLocation: null,
      reportedStartLocationId: null,
      startLocation: previousShiftEndLocation,
      startLocationId: previousShiftEndLocationId,
      priority: null,
      id: tempShiftId,
      projectId: null,
      ignoreStayNecessary: false,
      sharePlanNumber: null,
      maintenanceNumber: null,
      isHighlighted: true,
    };

    return {
      shiftWithoutActivities,
      newShift,
    };
  } else {
    console.error('No locationId provided or shiftToUpdate not found');
    return null;
  }
}
